<template>
  <div v-if="isVisible" class="tlm-overlay" @mousedown.self="closeModal">
    <div class="tlm-dialog">

      <!-- Header -->
      <div class="tlm-header">
        <div class="tlm-header-text">
          <h5 class="tlm-title">
            Tenant license
            <span v-if="tenant" class="tlm-tenant-name">— {{ tenant.tenantName || tenant.name }}</span>
          </h5>
          <p class="tlm-subtitle">Select which resources this tenant can access</p>
        </div>
        <button class="tlm-close-btn" @click="closeModal" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <!-- Toolbar: search + expand/collapse -->
      <div class="tlm-toolbar">
        <div class="tlm-search-box">
          <svg class="tlm-search-icon" width="14" height="14" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="7" stroke="#9ca3af" stroke-width="1.8"/>
            <path d="M14 14l4 4" stroke="#9ca3af" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search resource list..."
            class="tlm-search-input"
          />
        </div>
        <button class="tlm-btn-outline" @click="expandAll">Expand all</button>
        <button class="tlm-btn-outline" @click="collapseAll">Collapse all</button>
      </div>

      <!-- Resource tree -->
      <div class="tlm-tree-wrapper">

        <!-- Loading state -->
        <div v-if="isLoading" class="tlm-loading">
          <div class="tlm-spinner"></div>
          <span>Loading resources…</span>
        </div>

        <div v-else>
          <!-- "All resources" root row -->
          <label class="tlm-tree-row tlm-root-row">
            <button class="tlm-chevron-btn" type="button" @click.prevent="isAllSelected ? collapseAll() : expandAll()">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 5l3 3 3-3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <input
              type="checkbox"
              class="tlm-checkbox"
              :checked="isAllSelected"
              :indeterminate.prop="isIndeterminate"
              @change="toggleAll"
            />
            <span class="tlm-label tlm-label--root">All resources</span>
            <span class="tlm-badge">{{ allNodeIds.length }}</span>
          </label>

          <!-- Flat tree list (ordered, respects expand state) -->
          <template v-for="node in flatList" :key="node.id">
            <label
              class="tlm-tree-row"
              :class="node.children.length ? 'tlm-parent-row' : 'tlm-leaf-row'"
              :style="{ paddingLeft: (16 + node.depth * 20) + 'px' }"
            >
              <!-- Chevron (only for nodes that have children) -->
              <button
                v-if="node.children.length"
                class="tlm-chevron-btn"
                :class="{ 'tlm-chevron-btn--closed': !isExpanded(node.id) }"
                type="button"
                @click.prevent="toggleExpand(node.id)"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 5l3 3 3-3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <!-- Spacer for leaf nodes (no chevron) -->
              <span v-else class="tlm-chevron-spacer"></span>

              <!-- Checkbox -->
              <input
                type="checkbox"
                class="tlm-checkbox"
                :checked="isNodeSelected(node)"
                :indeterminate.prop="isNodeIndeterminate(node)"
                @change="toggleNode(node)"
              />

              <!-- Label -->
              <span
                class="tlm-label"
                :class="node.children.length ? 'tlm-label--parent' : 'tlm-label--leaf'"
              >{{ node.name }}</span>

              <!-- Child count badge (only for parent nodes) -->
              <span v-if="node.children.length" class="tlm-badge">
                {{ node.children.length }}
              </span>
            </label>
          </template>

          <!-- Empty state -->
          <div v-if="flatList.length === 0" class="tlm-empty">
            No resources match your search.
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="tlm-footer">
        <div class="tlm-footer-note">
          <p>New tenants are licensed for every resource by default.</p>
          <p>Uncheck anything this tenant shouldn't access.</p>
        </div>
        <div class="tlm-footer-actions">
          <button class="tlm-btn-cancel" @click="closeModal" :disabled="isSaving">
            Cancel
          </button>
          <button class="tlm-btn-save" @click="saveChanges" :disabled="isSaving">
            <span v-if="!isSaving">Save changes</span>
            <span v-else>Saving…</span>
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script src="./tenantLicenseModel.js"></script>

<style scoped>
/* ── Overlay ──────────────────────────────────── */
.tlm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

/* ── Dialog ───────────────────────────────────── */
.tlm-dialog {
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 580px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

/* ── Header ───────────────────────────────────── */
.tlm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 15px 15px 11px;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
  flex-shrink: 0;
}

.tlm-title {
  font-size: 11px;
  font-weight: 700;
  color:#6a5d3c;
  margin: 0 0 4px;
  line-height: 1.3;
}

.tlm-tenant-name {
  color: #41081b;
  font-weight: 600;
}

.tlm-subtitle {
  font-size: 10px;
  color: #6b7280;
  margin: 0;
}

.tlm-close-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}
.tlm-close-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

/* ── Toolbar ──────────────────────────────────── */
.tlm-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.tlm-search-box {
  flex: 1;
  position: relative;
}

.tlm-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.tlm-search-input {
  width: 100%;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 12px 0 34px;
  font-size: 11px;
  color: #374151;
  outline: none;
  transition: border-color 0.2s;
}
.tlm-search-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}
.tlm-search-input::placeholder {
  color: #9ca3af;
}

.tlm-btn-outline {
  height: 28px;
  padding: 0 14px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;
}
.tlm-btn-outline:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

/* ── Tree wrapper ─────────────────────────────── */
.tlm-tree-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0 8px;
}

/* ── Loading ──────────────────────────────────── */
.tlm-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px;
  font-size: 12px;
  color: #6b7280;
}

.tlm-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Tree rows ────────────────────────────────── */
.tlm-tree-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 16px 5px 16px;
  cursor: pointer;
  user-select: none;
  transition: background 0.1s;
}
.tlm-tree-row:hover {
  background: #f9fafb;
}

/* Root "All resources" row */
.tlm-root-row {
  padding: 8px 16px;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 2px;
}

/* Parent node row (has children) */
.tlm-parent-row {
  background: transparent;
}
.tlm-parent-row:hover {
  background: #f0f9ff;
}

/* Leaf row (no children) */
.tlm-leaf-row {
  /* depth-based paddingLeft set via inline style */
}

/* ── Chevron ──────────────────────────────────── */
.tlm-chevron-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: transform 0.2s;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}
.tlm-chevron-btn--closed {
  transform: rotate(-90deg);
}
.tlm-chevron-btn:hover {
  color: #374151;
}

.tlm-chevron-spacer {
  width: 18px;
  flex-shrink: 0;
}

/* ── Checkbox ─────────────────────────────────── */
.tlm-checkbox {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: #2563eb;
  flex-shrink: 0;
}

/* ── Labels ───────────────────────────────────── */
.tlm-label {
  font-size: 11px;
  color: #374151;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tlm-label--root {
  font-weight: 700;
  font-size: 11px;
  color: #111827;
}
.tlm-label--parent {
  font-weight: 600;
  color: #1f2937;
}
.tlm-label--leaf {
  font-weight: 400;
  color: #4b5563;
}

/* ── Badge ────────────────────────────────────── */
.tlm-badge {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 20px;
  padding: 1px 8px;
  min-width: 22px;
  text-align: center;
  flex-shrink: 0;
}

/* ── Empty state ──────────────────────────────── */
.tlm-empty {
  padding: 24px 16px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
}

/* ── Footer ───────────────────────────────────── */
.tlm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  border-top: 1px solid #e5e7eb;
  gap: 12px;
  background: #fafafa;
  flex-shrink: 0;
}

.tlm-footer-note {
  flex: 1;
}
.tlm-footer-note p {
  margin: 0;
  font-size: 9px;
  color: #371212;
  line-height: 1.5;
}

.tlm-footer-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.tlm-btn-cancel {
  height: 30px;
  padding: 0 16px;
  font-size: 8px;
  font-weight: 500;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.tlm-btn-cancel:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}
.tlm-btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tlm-btn-save {
  height: 30px;
  padding: 0 16px;
  font-size: 8px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  background: #63492d;
  color: #ffffff;
  cursor: pointer;
  transition: background 0.15s;
}
.tlm-btn-save:hover:not(:disabled) {
  background: #15803d;
}
.tlm-btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
